import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, Paperclip, X, Download, FileText, BellOff, Bell } from "lucide-react";

function formatTime(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

/**
 * Recruiter-side message dialog scoped to a single candidate.
 * - Resolves (or creates) a thread on first send for (candidate, company).
 * - Polls every 8s while open.
 */
export default function MessageThreadDialog({ candidate, onClose }) {
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    // Phase 152 — pending attachments the recruiter has uploaded for the next send
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const bottomRef = useRef(null);
    const pollRef = useRef(null);

    const pickFiles = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const uploads = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                if (thread?.id) fd.append("thread_id", thread.id);
                const { data } = await api.post("/messages/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploads.push(data);
            }
            setPending((p) => [...p, ...uploads]);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const removePending = (id) => setPending((p) => p.filter((x) => x.id !== id));

    const downloadAttachment = async (att) => {
        try {
            const r = await api.get(`/messages/attachments/${att.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    // Phase 153 — inline preview URLs for images + PDFs (blob URL cached per id).
    // Loaded in a useEffect keyed on messages (NOT during render) so the 8s
    // poll doesn't fire a fresh download per re-render.
    const [previewUrls, setPreviewUrls] = useState({});
    const inflightRef = useRef(new Set());
    useEffect(() => {
        const toLoad = [];
        for (const m of messages) {
            for (const a of (m.attachments || [])) {
                const isImage = (a.content_type || "").startsWith("image/");
                const isPdf = (a.content_type || "") === "application/pdf";
                if ((isImage || isPdf) && !previewUrls[a.id] && !inflightRef.current.has(a.id)) {
                    toLoad.push(a);
                    inflightRef.current.add(a.id);
                }
            }
        }
        toLoad.forEach(async (a) => {
            try {
                const r = await api.get(`/messages/attachments/${a.id}/download`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                setPreviewUrls((p) => ({ ...p, [a.id]: url }));
            } catch { /* silent */ }
        });
    }, [messages, previewUrls]);
    useEffect(() => () => {
        // Revoke blob URLs on unmount so we don't leak memory
        for (const url of Object.values(previewUrls)) {
            try { URL.revokeObjectURL(url); } catch { /* noop */ }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadExisting = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/messages/threads");
            const match = (data || []).find((t) => t.candidate_id === candidate.id);
            if (match) {
                const { data: full } = await api.get(`/messages/threads/${match.id}`);
                setThread(full);
                setMessages(full.messages || []);
            }
        } catch (err) {
            // Non-fatal — empty state is fine
            console.warn(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadExisting();
        // eslint-disable-next-line
    }, [candidate.id]);

    useEffect(() => {
        // Poll while a thread exists
        if (!thread) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/messages/threads/${thread.id}`);
                setMessages(data.messages || []);
            } catch { /* ignore */ }
        }, 8000);
        return () => clearInterval(pollRef.current);
    }, [thread]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body && pending.length === 0) return;
        setSending(true);
        try {
            const attachment_ids = pending.map((p) => p.id);
            if (!thread) {
                const { data } = await api.post("/messages/threads", { candidate_id: candidate.id, body: body || "(sent an attachment)", attachment_ids });
                setThread(data);
                const { data: full } = await api.get(`/messages/threads/${data.id}`);
                setMessages(full.messages || []);
            } else {
                const { data: msg } = await api.post(`/messages/threads/${thread.id}/reply`, { body: body || "(sent an attachment)", attachment_ids });
                setMessages((m) => [...m, msg]);
            }
            setDraft("");
            setPending([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSending(false); }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e);
    };

    // Phase 153 — snooze thread
    const snooze = async (preset) => {
        if (!thread?.id) return toast.error("Send a first message before snoozing");
        try {
            if (preset === "clear") {
                await api.post(`/messages/threads/${thread.id}/snooze`, { until: null });
                setThread((t) => ({ ...(t || {}), snoozed_until: null }));
                toast.success("Thread un-snoozed");
            } else {
                const { data } = await api.post(`/messages/threads/${thread.id}/snooze-preset`, {
                    preset,
                    tz_offset_min: new Date().getTimezoneOffset(),
                });
                setThread((t) => ({ ...(t || {}), snoozed_until: data.snoozed_until }));
                toast.success(`Snoozed until ${new Date(data.snoozed_until).toLocaleString()}`);
            }
            setSnoozeOpen(false);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const isSnoozed = thread?.snoozed_until && new Date(thread.snoozed_until) > new Date();

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="rounded-none max-w-2xl p-0 gap-0" data-testid="message-thread-dialog">
                <DialogHeader className="p-5 pr-14 border-b border-border space-y-1">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="font-display font-bold text-xl flex items-center gap-2">
                                <Mail className="w-4 h-4 text-ikb" />
                                Message {candidate.name || "candidate"}
                            </DialogTitle>
                            <div className="text-xs font-mono text-muted-foreground mt-1">
                                {candidate.email || "no email on file"} {thread ? `· thread since ${formatTime(thread.created_at)}` : "· new conversation"}
                            </div>
                        </div>
                        {thread && (
                            <div className="relative shrink-0">
                                <Button
                                    type="button"
                                    variant={isSnoozed ? "default" : "outline"}
                                    size="sm"
                                    className={`rounded-none h-8 text-[11px] font-mono uppercase tracking-widest ${isSnoozed ? "bg-amber-500 hover:bg-amber-600 border-amber-500 text-white" : ""}`}
                                    onClick={() => setSnoozeOpen((o) => !o)}
                                    data-testid="msg-snooze-btn"
                                >
                                    {isSnoozed ? <BellOff className="w-3 h-3 mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                                    {isSnoozed ? "Snoozed" : "Snooze"}
                                </Button>
                                {snoozeOpen && (
                                    <div className="absolute right-0 top-9 bg-white border border-border rounded-md shadow-md py-1 z-10 w-48" data-testid="msg-snooze-menu">
                                        <button type="button" onClick={() => snooze("later_today")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary" data-testid="msg-snooze-later">Later today</button>
                                        <button type="button" onClick={() => snooze("tomorrow_morning")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary" data-testid="msg-snooze-tomorrow">Tomorrow 9am</button>
                                        <button type="button" onClick={() => snooze("next_tuesday")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary" data-testid="msg-snooze-tuesday">Next Tuesday 9am</button>
                                        <button type="button" onClick={() => snooze("next_week")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary" data-testid="msg-snooze-week">Next week</button>
                                        {isSnoozed && (
                                            <>
                                                <div className="border-t border-border my-1" />
                                                <button type="button" onClick={() => snooze("clear")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary text-rose-700" data-testid="msg-snooze-clear">Clear snooze</button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {isSnoozed && (
                        <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1 font-mono inline-flex items-center gap-1" data-testid="msg-snoozed-banner">
                            <BellOff className="w-3 h-3" /> Snoozed until {new Date(thread.snoozed_until).toLocaleString()}
                        </div>
                    )}
                </DialogHeader>

                <div className="bg-secondary/40 max-h-[50vh] min-h-[260px] overflow-y-auto p-5 space-y-3" data-testid="message-list">
                    {loading ? (
                        <div className="text-xs text-muted-foreground font-mono">Loading...</div>
                    ) : messages.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-12">
                            No messages yet. Say hello below — your message will reach the candidate via their seeker portal.
                        </div>
                    ) : messages.map((m) => {
                        const mine = m.sender_role !== "seeker";
                        return (
                            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[80%] border px-3 py-2 ${mine ? "bg-ikb text-white border-ikb" : "bg-white border-border"}`}>
                                    <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                        {m.sender_name || (mine ? "You" : "Candidate")} · {formatTime(m.created_at)}
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                                    {(m.attachments || []).length > 0 && (
                                        <div className="mt-1.5 space-y-1.5" data-testid={`msg-attachments-${m.id}`}>
                                            <div className="flex flex-wrap gap-1.5">
                                                {m.attachments.map((a) => {
                                                    return (
                                                        <button
                                                            key={a.id}
                                                            type="button"
                                                            onClick={() => downloadAttachment(a)}
                                                            className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border rounded-md max-w-[220px] truncate ${mine ? "border-white/40 hover:bg-white/10" : "border-border hover:bg-secondary/80"}`}
                                                            title={`${a.filename} · ${Math.round((a.size || 0) / 1024)} KB`}
                                                            data-testid={`msg-attachment-${a.id}`}
                                                        >
                                                            <FileText className="w-3 h-3 shrink-0" />
                                                            <span className="truncate">{a.filename}</span>
                                                            <Download className="w-3 h-3 shrink-0 opacity-70" />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {m.attachments.map((a) => {
                                                const isImage = (a.content_type || "").startsWith("image/");
                                                const isPdf = (a.content_type || "") === "application/pdf";
                                                const url = previewUrls[a.id];
                                                if (!url) return null;
                                                if (isImage) {
                                                    return (
                                                        <img key={`p-${a.id}`} src={url} alt={a.filename} className="max-w-full max-h-72 rounded border border-border bg-white" data-testid={`msg-attachment-preview-${a.id}`} />
                                                    );
                                                }
                                                if (isPdf) {
                                                    return (
                                                        <iframe key={`p-${a.id}`} src={url} title={a.filename} className="w-full h-72 rounded border border-border bg-white" data-testid={`msg-attachment-preview-${a.id}`} />
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>

                <form onSubmit={send} className="border-t border-border p-4 bg-white">
                    <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Write a message...  (Cmd/Ctrl+Enter to send)"
                        rows={3}
                        className="rounded-none resize-none"
                        data-testid="message-input"
                    />
                    {pending.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="msg-pending-attachments">
                            {pending.map((p) => (
                                <span key={p.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 bg-secondary border border-border rounded-md">
                                    <FileText className="w-3 h-3" />
                                    <span className="max-w-[160px] truncate">{p.filename}</span>
                                    <button type="button" onClick={() => removePending(p.id)} className="hover:text-destructive" aria-label="Remove" data-testid={`msg-remove-attachment-${p.id}`}>
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                        <input ref={fileRef} type="file" hidden multiple onChange={(e) => pickFiles(e.target.files)} data-testid="msg-attach-input" />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            className="rounded-none"
                            data-testid="msg-attach-btn"
                        >
                            <Paperclip className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Attach"}
                        </Button>
                        <Button type="submit" disabled={sending || (!draft.trim() && pending.length === 0)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="message-send-btn">
                            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
